import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid, Card, CardContent, FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { useGlobalDistribucion } from "../hooks/useGlobalDistribucion";
import { getCompetenciasAlertas, getProgramasConResultadosPruebas } from "../services/alertasGenericasService";
import GraficaDistribucionBoxPlot from "../components/Global/GraficaDistribucionBoxPlot";
import TareasTerminadas from "../../general/TareasTerminadas";
import emma_w from "../../../global/imagenes/emma_w.png";
import { normalizePrograma } from "../utils/distribucionPrograma";

const PROGRAMA_DEFAULT = "Ingeniería de sistemas";

const cardSx = {
  borderRadius: 12,
  boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
  height: "100%",
};

const Global = () => {
  const [opcionesPrograma, setOpcionesPrograma] = useState([]);
  const [loadingProgramas, setLoadingProgramas] = useState(true);
  const [programa, setPrograma] = useState("");
  const [filtroCompetencia, setFiltroCompetencia] = useState("RC");
  const [opcionesCompetencia, setOpcionesCompetencia] = useState([]);

  const { loading: loadingDistribucion, puntajes, stats } = useGlobalDistribucion(programa, filtroCompetencia);

  useEffect(() => {
    let activo = true;
    getCompetenciasAlertas().then((lista) => {
      if (!activo) return;
      setOpcionesCompetencia(Array.isArray(lista) ? lista : []);
    });
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    setLoadingProgramas(true);
    getProgramasConResultadosPruebas({ competencia: filtroCompetencia })
      .then((list) => {
        if (!activo) return;
        const ordenados = Array.isArray(list)
          ? [...list].sort((a, b) => String(a).localeCompare(String(b)))
          : [];
        setOpcionesPrograma(ordenados);
        setLoadingProgramas(false);
      })
      .catch(() => {
        if (!activo) return;
        setOpcionesPrograma([]);
        setLoadingProgramas(false);
      });
    return () => {
      activo = false;
    };
  }, [filtroCompetencia]);

  useEffect(() => {
    if (!opcionesPrograma.length) {
      setPrograma("");
      return;
    }
    setPrograma((prev) => {
      const prevTrim = (prev || "").trim();
      if (prevTrim && opcionesPrograma.some((p) => normalizePrograma(p) === normalizePrograma(prevTrim))) {
        return prevTrim;
      }
      const def = opcionesPrograma.find((p) => normalizePrograma(p) === normalizePrograma(PROGRAMA_DEFAULT));
      return def || opcionesPrograma[0] || "";
    });
  }, [opcionesPrograma]);

  const programaListo = Boolean((programa || "").trim());

  return (
    <Box pb={1} pt={0.25} style={{ maxWidth: 1360, margin: "0 auto", paddingLeft: 12, paddingRight: 12 }}>
      <Typography variant="h6" component="h1" style={{ fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
        Distribución del desempeño
      </Typography>

      <Box mb={2} display="flex" flexWrap="wrap" gap={4} alignItems="flex-end" style={{ maxWidth: 720 }}>
        <Box style={{ minWidth: 180, flex: 1 }}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel style={{ fontSize: 12 }}>Competencia</InputLabel>
            <Select
              value={filtroCompetencia || "RC"}
              onChange={(e) => setFiltroCompetencia(e.target.value)}
              label="Competencia"
              style={{ height: 40, fontSize: 13 }}
            >
              {opcionesCompetencia.map((item) => (
                <MenuItem key={item.codigo} value={item.codigo} style={{ fontSize: 13 }}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box style={{ minWidth: 260, flex: 2, marginLeft: 16 }}>
          <Autocomplete
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            fullWidth
            size="small"
            disabled={loadingProgramas && !opcionesPrograma.length}
            options={opcionesPrograma}
            value={programa}
            loading={loadingProgramas}
            filterOptions={(options, params) => {
              const q = (params.inputValue || "").trim().toLowerCase();
              if (!q) return options;
              return options.filter((o) => String(o).toLowerCase().includes(q));
            }}
            onChange={(e, newValue) => {
              setPrograma(newValue != null ? String(newValue) : "");
            }}
            onInputChange={(e, newInputValue, reason) => {
              if (reason === "input" || reason === "clear") {
                setPrograma(newInputValue != null ? String(newInputValue) : "");
              }
            }}
            getOptionLabel={(option) => (typeof option === "string" ? option : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Programa"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true, style: { fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 } }}
                inputProps={{ ...params.inputProps, style: { fontSize: 14 } }}
              />
            )}
          />
        </Box>
      </Box>

      {!loadingProgramas && !opcionesPrograma.length ? (
        <Typography variant="body2" color="textSecondary">
          No hay programas con resultados de pruebas. Verifica el servicio de integraciones.
        </Typography>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" style={{ gap: 10, height: "100%" }}>
              <Card variant="outlined" style={cardSx}>
                <CardContent style={{ padding: "12px 14px" }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                    Eje vertical (nota)
                  </Typography>
                  <Typography variant="body2" style={{ color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
                    Cada posición corresponde a la <strong>nota total</strong> del último intento: 0 abajo, 5 arriba. Más arriba =
                    mejor desempeño.
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" style={cardSx}>
                <CardContent style={{ padding: "12px 14px" }}>
                  <Typography variant="body2" style={{ color: "#475569", fontSize: 13, lineHeight: 1.65, marginBottom: 14 }}>
                    <Box component="span" style={{ color: "#b91c1c", fontWeight: 800 }}>
                      Rojo
                    </Box>
                    : notas más bajas del programa; estos estudiantes están muy por debajo del Q1.
                  </Typography>
                  <Typography variant="body2" style={{ color: "#475569", fontSize: 13, lineHeight: 1.65 }}>
                    <Box component="span" style={{ color: "#15803d", fontWeight: 800 }}>
                      Verde
                    </Box>
                    : los estudiantes con los mejores promedios de su programa.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box bgcolor="#ffffff" borderRadius={14} boxShadow="0 1px 8px rgba(0, 0, 0, 0.08)" p={2} height="100%" minHeight={360}>
              {!programaListo ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <Typography variant="body2" color="textSecondary">
                    Selecciona un programa para ver la distribución.
                  </Typography>
                </Box>
              ) : loadingDistribucion || !stats ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <TareasTerminadas
                    mensaje="Cargando distribución"
                    imagen={emma_w}
                    widthImg="7%"
                    marginTop="0"
                    cargando
                  />
                </Box>
              ) : (
                <GraficaDistribucionBoxPlot programaNombre={programa} puntajes={puntajes} stats={stats} />
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Global;
